import React, { useState } from 'react';
import { Container, Stack, Title, Text, Card, Group, Button, TextInput, Avatar, Badge, Divider } from '@mantine/core';
import { IconUser, IconMail, IconCalendar, IconEdit, IconCheck, IconX } from '@tabler/icons-react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from '@mantine/form';

const Account = () => {
  const { user, isAdmin } = useAuth();
  const [editing, setEditing] = useState(false);

  const form = useForm({
    initialValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      username: user?.username || '',
    },
    validate: {
      firstName: (value) => (value.length < 2 ? 'First name must be at least 2 characters' : null),
      lastName: (value) => (value.length < 2 ? 'Last name must be at least 2 characters' : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      username: (value) => (value.length < 3 ? 'Username must be at least 3 characters' : null),
    },
  });

  const handleSave = (values) => {
    // TODO: Implement update user profile
    console.log('Updating profile:', values);
    setEditing(false);
  };

  const handleCancel = () => {
    form.reset();
    setEditing(false);
  };

  if (!user) {
    return (
      <Container size="md">
        <Title order={1} ta="center" c="green">Please log in to view your account</Title>
      </Container>
    );
  }

  return (
    <Container size="md">
      <Stack gap="xl">
        <Title order={1} c="green">My Account</Title>
        
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Group gap="md">
              <Avatar size="lg" color="green">
                {user.firstName ? user.firstName.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase()}
              </Avatar>
              <div>
                <Title order={3}>
                  {user.firstName ? `${user.firstName} ${user.lastName}` : user.username}
                </Title>
                <Group gap="xs">
                  <Badge color="green" variant="light">
                    {user.role || 'User'}
                  </Badge>
                  {isAdmin() && (
                    <Badge color="red" variant="filled">
                      Admin
                    </Badge>
                  )}
                </Group>
              </div>
            </Group>
            
            {!editing && (
              <Button
                leftSection={<IconEdit size={16} />}
                color="green"
                variant="outline"
                onClick={() => setEditing(true)}
              >
                Edit Profile
              </Button>
            )}
          </Group>
          
          <Divider mb="md" />
          
          {editing ? (
            <form onSubmit={form.onSubmit(handleSave)}>
              <Stack gap="md">
                <Group grow>
                  <TextInput
                    label="First Name"
                    {...form.getInputProps('firstName')}
                  />
                  <TextInput
                    label="Last Name"
                    {...form.getInputProps('lastName')}
                  />
                </Group>
                
                <TextInput
                  label="Username"
                  {...form.getInputProps('username')}
                />
                
                <TextInput
                  label="Email"
                  {...form.getInputProps('email')}
                />
                
                <Group justify="flex-end" gap="sm">
                  <Button
                    variant="outline"
                    leftSection={<IconX size={16} />}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="green"
                    leftSection={<IconCheck size={16} />}
                  >
                    Save Changes
                  </Button>
                </Group>
              </Stack>
            </form>
          ) : (
            <Stack gap="md">
              <Group>
                <IconUser size={20} color="var(--mantine-color-green-6)" />
                <div>
                  <Text size="sm" c="dimmed">Full Name</Text>
                  <Text fw={500}>
                    {user.firstName ? `${user.firstName} ${user.lastName}` : 'Not provided'}
                  </Text>
                </div>
              </Group>
              
              <Group>
                <IconUser size={20} color="var(--mantine-color-green-6)" />
                <div>
                  <Text size="sm" c="dimmed">Username</Text>
                  <Text fw={500}>{user.username}</Text>
                </div>
              </Group>
              
              <Group>
                <IconMail size={20} color="var(--mantine-color-green-6)" />
                <div>
                  <Text size="sm" c="dimmed">Email</Text>
                  <Text fw={500}>{user.email}</Text>
                </div>
              </Group>
              
              <Group>
                <IconCalendar size={20} color="var(--mantine-color-green-6)" />
                <div>
                  <Text size="sm" c="dimmed">Member Since</Text>
                  <Text fw={500}>
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                  </Text>
                </div>
              </Group>
            </Stack>
          )}
        </Card>
        
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={3} mb="md">Account Statistics</Title>
          <Group grow>
            <div style={{ textAlign: 'center' }}>
              <Text size="xl" fw={700} c="green">0</Text>
              <Text size="sm" c="dimmed">Favorites</Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text size="xl" fw={700} c="green">0</Text>
              <Text size="sm" c="dimmed">Saved Searches</Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <Text size="xl" fw={700} c="green">0</Text>
              <Text size="sm" c="dimmed">Properties Viewed</Text>
            </div>
          </Group>
        </Card>
      </Stack>
    </Container>
  );
};

export default Account;
